import { GCSUploadOptions, uploadToGCS } from 'cio/utils/uploads/gcs';
import { S3UploadOptions, uploadToS3 } from 'cio/utils/uploads/s3';

const FILE_EXTENSION_REGEXP = /(?:\.([^.]+))?$/;

export type SignResponse = { gcs: GCSUploadOptions } | { s3: S3UploadOptions };

export type UploaderFn = (
  file: File,
  json: GCSUploadOptions | S3UploadOptions,
  opts: { processData?: (data: FormData) => void },
) => Promise<{ key: string }>;

export function getUploadFn(
  signResponse: SignResponse,
  fileType?: string,
): {
  uploadFn: UploaderFn;
  data: GCSUploadOptions | S3UploadOptions;
} {
  // Historically, we've uploaded files directly to S3. For the EU datacenter,
  // we're uploading to GCS. Over time, we plan to drop S3 entirely.
  //
  // In the interim, we're uploading to both. Any environment in the EU
  // datacenter will upload to GCS; others will upload to S3. To do that, we
  // just look at the response the backend sends us for the "sign" endpoint,
  // and if it's a GCS bucket we try to upload to GCS.
  if ('gcs' in signResponse) {
    return { uploadFn: uploadToGCS, data: signResponse.gcs };
  } else {
    let data = { ...signResponse.s3 };
    if (fileType != null) {
      data['Content-Type'] = fileType;
    }

    return { uploadFn: uploadToS3, data };
  }
}

export function blobToDataUrl(file: File) {
  return new Promise((resolve) => {
    let reader = new FileReader();

    reader.addEventListener(
      'load',
      function () {
        resolve(reader.result);
      },
      false,
    );

    reader.readAsDataURL(file);
  });
}

export async function dataUrlToFile(filename: string, dataUrl: string) {
  let blob = await (await fetch(dataUrl)).blob();
  let fileType = dataUrl.substring(dataUrl.indexOf(':') + 1, dataUrl.indexOf(';'));

  return new File([blob], filename, { type: fileType });
}

export function getFileExtension(filename: string) {
  let fileExtensionResult = FILE_EXTENSION_REGEXP.exec(filename);
  return fileExtensionResult == null ? '' : fileExtensionResult[1];
}
